<template>
    <v-row>
        <v-col v-if="'title' in survey">
            <v-alert v-if="isPreview" type="info" variant="tonal" density="compact" class="mb-2">
                Preview mode — submission disabled
            </v-alert>
            <v-card>
                <v-card-text>
                    <h6 class="text-h6 font-weight-bold">
                        {{ survey.title }} <br />
                        <small><span class="text-red">From: {{ moment(survey.startDate).format("D-MMM-yyyy") }} To: {{
                            moment(survey.endDate).format("D-MMM-yyyy") }}</span></small>
                    </h6>
                    <h6 class="text-subtitle-1" v-html="survey.description"></h6>
                </v-card-text>
            </v-card>
            <v-card class="my-2">
                <template v-for="(ques, i) in survey.questions" :key="ques.questionID">
                    <v-card-text v-if="shouldShowSection(ques, i)">
                        <h2 class="text-h6 font-weight-bold">{{ ques.section }}</h2>
                        <v-divider class="my-2" />
                    </v-card-text>
                    <v-card-text>
                        <h3 class="mb-2">
                            {{ fillPlaceholders(ques.question) }} <small class="text-red font-weight-bold"
                                v-if="ques.isRequired">*Required</small> <br />
                            <small v-html="ques.description"></small>
                        </h3>
                        <v-text-field v-if="ques.questionType == 'Textbox'"
                            @change="saveAnswer(ques, $event.target.value)" />

                        <v-text-field type="number" v-if="ques.questionType == 'Number'"
                            @change="saveAnswer(ques, $event.target.value)" />

                        <v-autocomplete v-model="dropDownAnswers[ques.questionID]" :items="getOptions(ques)"
                            v-if="ques.questionType.includes('Select')"
                            :multiple="ques.questionType == 'MultiSelect'" />

                        <template v-if="ques.questionType == 'Checkbox'">
                            <v-checkbox v-for="opt in getOptions(ques)" :key="opt" :label="opt"
                                :value="opt" v-model="dropDownAnswers[ques.questionID]" density="compact" hide-details="auto" />
                        </template>

                        <VueDatePicker v-model="dropDownAnswers[ques.questionID]" :enable-time-picker="false" auto-apply
                            format="dd/MMM/yyyy" :text-input="{ format: 'dd/MMM/yyyy' }" model-type="dd-MMM-yyyy"
                            placeholder="dd/MMM/yyyy" v-if="ques.questionType == 'DatePicker'" />

                        <v-radio-group v-if="ques.questionType == 'RadioGroup'"
                            @change="saveAnswer(ques, $event.target.value)">
                            <template v-for="opt in getOptions(ques)" :key="opt">
                                <v-radio :label="opt" :value="opt" />
                            </template>
                        </v-radio-group>

                        <v-file-input
                            label="Select File"
                            v-if="ques.questionType == 'FileUpload'"
                            density="compact"
                            variant="outlined"
                            multiple
                            :accept="getFileAcceptAttr(ques.options) || undefined"
                            :hint="getFileAcceptHint(ques.options, ques.maxFileSizeMb) || undefined"
                            :persistent-hint="Boolean(getFileAcceptHint(ques.options, ques.maxFileSizeMb))"
                            @change="onFileChange(ques, $event.target.files)"
                        />

                        <v-divider v-if="i < survey.questions.length - 1" />
                    </v-card-text>
                </template>
            </v-card>
            <v-card v-if="!isPreview">
                <v-card-text align="end">
                    <v-btn @click="submit">Submit</v-btn>
                </v-card-text>
            </v-card>
        </v-col>
        <v-col v-else>
            <v-card>
                <v-card-text>
                    <h6 class="text-h6 font-weight-bold text-red">Survey form is not available</h6>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script setup>
import { ref, reactive, inject, onMounted, markRaw, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getById, getSurvey, addDetail, addFiles, getFileAcceptAttr, getFileAcceptHint, validateFilesAgainstAccept } from '@/services/surveyService';
import moment from 'moment';
import { useAuthStore } from "@/setup/stores/authStore";

const loader = inject("loader");
const snackbar = inject("snackBar");
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const surveyId = route.params.id;
const isPreview = computed(() => route.query.preview === '1');
const survey = ref({});
const answers = markRaw([]);
const dropDownAnswers = reactive({});
const files = ref([]);

async function getData() {
    loader.show();

    const fetchSurvey = isPreview.value ? getSurvey(surveyId) : getById(surveyId);

    let result = await fetchSurvey
        .catch(error => {
            console.log(error);
            snackbar.error(error);
        })
        .finally(async () => {
            loader.hide();
        });

    if (result) {
        survey.value = result;
        // Preview loads admin payload which includes inactive questions.
        survey.value.questions = (survey.value.questions || []).filter(
            (q) => q.isActive !== false
        );

        survey.value.questions.forEach(ques => {
            answers.push({ questionID: ques.questionID, answer: "" });

            if (ques.questionType == "MultiSelect")
                dropDownAnswers[ques.questionID] = [];

            if (ques.questionType == "Checkbox")
                dropDownAnswers[ques.questionID] = [];
        });

        survey.value.questions.filter(x => x.questionType == "FileUpload").forEach(ques => {
            files.value.push({ questionID: ques.questionID, files: [] });
        });
    }
}

function fillPlaceholders(question) {
    var vals = {
        FullName: authStore.user.username,
        Team: authStore.user.organization,
        Section: authStore.user.section,
        Jamaat: authStore.user.jamaat,
        Rank: authStore.user.rank,
        ItsId: authStore.user.itsId,
    };

    Object.keys(vals).forEach(key => {
        question = question.replace(`[${key}]`, vals[key]);
    });

    return question;
}

function shouldShowSection(ques, index) {
    if (!ques.section) return false;
    if (index === 0) return true;
    return ques.section !== survey.value.questions[index - 1].section;
}

function getOptions(ques) {
    if (!ques.options) return [];
    return ques.options.split(",");
}

function saveFile(ques, uploads) {
    let fl = files.value.find(x => x.questionID == ques.questionID);
    fl.files = uploads;
}

function onFileChange(ques, uploads) {
    const error = validateFilesAgainstAccept(uploads, ques.options, ques.maxFileSizeMb);
    if (error) {
        snackbar.error(error);
        saveFile(ques, []);
        return;
    }
    saveFile(ques, uploads);
}

function saveAnswer(ques, value) {
    let ans = answers.find(x => x.questionID == ques.questionID);
    ans.answer = value;
}

async function submit() {
    let hasError = false;

    survey.value.questions.forEach(ques => {
        let ans = answers.find(x => x.questionID == ques.questionID);
        let fl = files.value.find(x => x.questionID == ques.questionID);
        let dropdownans = dropDownAnswers[ques.questionID];

        if (dropdownans)
            ans.answer = dropdownans.toString();

        if (ques.isRequired && ques.questionType != "FileUpload" && !ans.answer) {
            hasError = true;
            snackbar.error(`Answer for question: ${ques.question} is required. Please enter the answer and submit the form.`);
        }

        if (ques.isRequired && ques.questionType == "FileUpload" && fl.files.length == 0) {
            hasError = true;
            snackbar.error(`File upload for question: ${ques.question} is required. Please select a file and submit the form.`);
        }

        if (ques.questionType == "FileUpload" && fl?.files?.length) {
            const typeError = validateFilesAgainstAccept(fl.files, ques.options, ques.maxFileSizeMb);
            if (typeError) {
                hasError = true;
                snackbar.error(typeError);
            }
        }
    });

    if (hasError)
        return;

    loader.show();

    if (files.value.length > 0) {
        const result = await addFiles(surveyId, files.value)
            .catch(error => {
                console.log(error);
                snackbar.error(error);
            });

        console.log(result);
    }

    addDetail(surveyId, answers)
        .then(() => {
            snackbar.success("Your form has been submitted successfully");
        })
        .catch(error => {
            console.log(error);
            snackbar.error(error);
        })
        .finally(async () => {
            loader.hide();
        });


    setTimeout(() => router.push("/home"), 1000);
}

onMounted(async () => {
    await getData();
})

</script>